import {
  PText,
  PHeadline,
  PFlex,
  PFlexItem,
  PContentWrapper,
  PSpinner,
  PPopover,
  PIcon
} from '@porsche-design-system/components-react'
import clsx from 'clsx'
import React from 'react'

import { ratingsColors } from '../../../shared/utils/ratingService'
import Rail from './Rail/Rail'

import styles from './ratingsidebar.module.css'

type Props = {
  data: any
  loading?: boolean
  average?: number
  averageHistory?: number
}

const RatingSidebar: React.FC<Props> = ({ data, loading, average, averageHistory }) => {
  const [difference, setDifference] = React.useState('')
  const [isNegative, setIsNegative] = React.useState(false)

  function getPercentage(starTotal: number) {
    if (starTotal !== undefined) {
      const percent = (starTotal / data?.count) * 100
      return percent.toFixed(0)
    }
    return '-'
  }

  React.useEffect(() => {
    if (average === undefined || averageHistory === undefined) {
      return
    }
    const diff = average - averageHistory
    if (diff > 0) {
      setDifference('+' + diff.toFixed(2))
    }
    if (diff < 0) {
      setIsNegative(true)
      setDifference(diff.toFixed(2))
    }
  }, [averageHistory])

  const addPercentageChange = () => {
    return (
      <>
        {!isNegative && (
          <>
            <PFlexItem>
              <PIcon name='play' size='small' color='notification-success' className={styles.up} />
            </PFlexItem>
            <PFlexItem>
              <PText weight='semibold' color='notification-success'>
                {difference}
              </PText>
            </PFlexItem>
          </>
        )}
        {isNegative && (
          <>
            <PFlexItem>
              <PIcon name='play' size='small' color='notification-error' className={styles.down} />
            </PFlexItem>
            <PFlexItem>
              <PText weight='semibold' color='notification-error'>
                {difference}
              </PText>
            </PFlexItem>
          </>
        )}
      </>
    )
  }

  return (
    <section className={clsx(styles.sidebar, { [styles.loading]: loading })}>
      <PFlex alignItems='center'>
        <PFlexItem>
          <PHeadline variant='headline-3' align='left'>
            {average === 0 || average === undefined ? '-' : average.toFixed(2)}
          </PHeadline>
        </PFlexItem>
        {addPercentageChange()}
      </PFlex>
      <PText>
        Gesamtbewertung
        <PPopover>
          Zur Berechnung der Entwicklung wird jeweils der ausgewählte Zeitraum mit dem, dem ausgewählten Zeitraum
          vorhergehenden, gleich langen Zeitraum verglichen.
        </PPopover>
      </PText>
      <PContentWrapper width='extended' className={styles.ratings}>
        <PFlex className={styles.starsLine}>
          <PFlexItem className={styles.stars}>
            <PText weight='semibold'>5 Sterne</PText>
          </PFlexItem>
          <PFlexItem className={styles.bar}>
            <Rail
              percentage={data && getPercentage(data?.five)}
              bgColor={ratingsColors.five}
              totalStarsRatings={data?.five}
            />
          </PFlexItem>
        </PFlex>
        <PFlex className={styles.starsLine}>
          <PFlexItem className={styles.stars}>
            <PText weight='semibold'>4 Sterne</PText>
          </PFlexItem>
          <PFlexItem className={styles.bar}>
            <Rail
              percentage={data && getPercentage(data?.four)}
              bgColor={ratingsColors.four}
              totalStarsRatings={data?.four}
            />
          </PFlexItem>
        </PFlex>
        <PFlex className={styles.starsLine}>
          <PFlexItem className={styles.stars}>
            <PText weight='semibold'>3 Sterne</PText>
          </PFlexItem>
          <PFlexItem className={styles.bar}>
            <Rail
              percentage={data && getPercentage(data?.three)}
              bgColor={ratingsColors.three}
              totalStarsRatings={data?.three}
            />
          </PFlexItem>
        </PFlex>
        <PFlex className={styles.starsLine}>
          <PFlexItem className={styles.stars}>
            <PText weight='semibold'>2 Sterne</PText>
          </PFlexItem>
          <PFlexItem className={styles.bar}>
            <Rail
              percentage={data && getPercentage(data?.two)}
              bgColor={ratingsColors.two}
              totalStarsRatings={data?.two}
            />
          </PFlexItem>
        </PFlex>
        <PFlex className={styles.starsLine}>
          <PFlexItem className={styles.stars}>
            <PText weight='semibold'>1 Stern</PText>
          </PFlexItem>
          <PFlexItem className={styles.bar}>
            <Rail
              percentage={data && getPercentage(data?.one)}
              bgColor={ratingsColors.one}
              totalStarsRatings={data?.one}
            />
          </PFlexItem>
        </PFlex>
      </PContentWrapper>
      {loading && <PSpinner className={styles.spinner} />}
    </section>
  )
}

export default RatingSidebar
